// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import useTranslation from "@/hooks/useTranslation";
import type { TyLead, TyLeadStage } from "@/types";
import Icon from "@/UI/atoms/Icon";
import SelectInput from "@/UI/inputs/SelectInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyLeadStageDetails = { lead: TyLead; onDone: () => void };

type Props = { onClose: () => void; details?: unknown };

/**
 * Moving a lead along. **Only the legal moves are offered** — the rest are not greyed out, they are
 * not there, because a control that lists six choices and refuses four of them is a quiz.
 *
 * What is legal depends on the role, and the list mirrors the backend rather than replacing it:
 *
 * - **`won` is never offered to anybody.** It follows a reservation or a contract.
 * - A **member** sees later stages only, plus `lost`. Their pipeline runs one way, which is what
 *   stops a worked lead being walked back to `new` and quietly closed.
 * - An **owner or manager** sees every stage, and may revive a finished lead.
 *
 * `lost` asks for a reason in the same step. A loss without one is the only number the funnel has,
 * thrown away.
 */
export default function LeadStagePopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { isOwner, role } = usePermission();
    const { lead, onDone } = details as TyLeadStageDetails;

    const canMoveFreely = isOwner || role === "manager";
    const working = constants.leadWorkingStages as readonly string[];
    const isTerminal = (constants.leadTerminalStages as readonly string[]).includes(lead.stage);

    /** Everything this person may move this lead to, from where it is now. */
    const allowed = constants.leadStages.filter((stage) => {
        if ((constants.leadSystemStages as readonly string[]).includes(stage)) return false;
        if (stage === lead.stage) return false;
        if (canMoveFreely) return true;
        if (isTerminal) return false;
        return stage === "lost" || working.indexOf(stage) > working.indexOf(lead.stage);
    });

    const [stage, setStage] = useState<string>(allowed[0] ?? "");
    const [lostReason, setLostReason] = useState<string>(constants.leadLostReasons[0]);

    const { loading, error, succeededAt, invoke } = useFetch(`/crm/leads/${lead._id}/stage`, { method: "PATCH" }, false);

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => invoke({ method: "PATCH", data: { stage, lostReason: stage === "lost" ? lostReason : undefined } });

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.crm.moveStage}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <p className="lead-stage__from">
                    {t.crm.fields.stage}: {t.crm.stages[lead.stage]}
                </p>

                {allowed.length === 0 ? (
                    <p className="lead-stage__hint">{isTerminal ? t.crm.wonHint : t.crm.backwardsHint}</p>
                ) : (
                    <>
                        <SelectInput
                            value={stage}
                            onChange={setStage}
                            options={allowed.map((item) => ({ value: item, label: t.crm.stages[item as TyLeadStage] }))}
                            label={t.crm.fields.stage}
                            disabled={loading}
                        />

                        {stage === "lost" && (
                            <>
                                <SelectInput
                                    value={lostReason}
                                    onChange={setLostReason}
                                    options={constants.leadLostReasons.map((item) => ({ value: item, label: t.crm.lostReasons[item] }))}
                                    label={t.crm.fields.lostReason}
                                    disabled={loading}
                                />
                                <p className="lead-stage__hint">{t.crm.lostHint}</p>
                            </>
                        )}

                        {!canMoveFreely && <p className="lead-stage__hint">{t.crm.backwardsHint}</p>}
                    </>
                )}

                {error && <p className="side-form__error">{error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading || !allowed.length}>
                    {loading ? t.common.loading : t.common.save}
                </button>
            </footer>
        </section>
    );
}
